import React, { useEffect, useState } from 'react';
import { Button, CircularProgress, Dialog } from '@mui/material';
import { signOut } from '@aws-amplify/auth';
import CheckIcon from '@mui/icons-material/Check';
import ThemeLogo from '../Component/ThemeLogo';
import { NodeSection } from '../NodeUI';
import useBillingStore from '../../stores/useBillingStore';
import useProjectStore from '../ProjectCanvas/useProjectStore';
import { StyledSnackbar, useSnackbar } from '../common/AppSnackbar';
import OnboardingProviderKeyForm from './OnboardingProviderKeyForm';
import { PLAN_CATALOG } from './planFeatures';
import './billing.css';

const STANDARD_HIGHLIGHTS = PLAN_CATALOG.STANDARD.features.slice(0, 5);

const PENDING_INTRO = [
  'Add an API key from OpenAI, Anthropic, or Google to unlock Free and get 500 credits.',
  'Your provider bills you for model usage; Workflow86 charges credits only for orchestration.',
];

const INVALID_BODY =
  "We couldn't use your provider API key. Update it below, or upgrade to Standard to use Workflow86-managed keys.";

export default function FreeTierOnboardingGate() {
  const { show, snackbarProps } = useSnackbar();
  const billingPlan = useProjectStore((s) => s.billingPlan);
  const freeTierState = useProjectStore((s) => s.billingFreeTierState);
  const subscription = useBillingStore((s) => s.subscription);
  const validateProviderKey = useBillingStore((s) => s.validateProviderKey);
  const startCheckout = useBillingStore((s) => s.startCheckout);
  const awaitCheckoutSettled = useBillingStore((s) => s.awaitCheckoutSettled);

  const [validating, setValidating] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  // Returning from Stripe Checkout: the plan flips only once the webhook lands, so show a
  // finalizing state (no Upgrade button) until it does — prevents duplicate checkouts.
  const [settling, setSettling] = useState(
    () => new URLSearchParams(window.location.search).get('checkout') === 'success'
  );

  useEffect(() => {
    if (!settling) return undefined;
    let cancelled = false;
    (async () => {
      await awaitCheckoutSettled();
      if (cancelled) return;
      setSettling(false);
      const params = new URLSearchParams(window.location.search);
      params.delete('checkout');
      const next = `${window.location.pathname}${params.toString() ? `?${params}` : ''}`;
      window.history.replaceState({}, '', next);
    })();
    return () => {
      cancelled = true;
    };
  }, [settling, awaitCheckoutSettled]);

  const state = freeTierState || subscription?.freeTier?.state;
  const gated =
    billingPlan === 'FREE' && (state === 'PENDING_BYOK' || state === 'BYOK_INVALID');

  const isInvalid = state === 'BYOK_INVALID';

  const handleValidated = async ({ credentialId }) => {
    // validateProviderKey already flips freeTierState to UNLOCKED and refreshes the store.
    const result = await validateProviderKey({ credentialId });
    if (result?.unlocked) {
      show("You're all set — 500 credits added.", 'success');
    }
  };

  const handleLogout = async () => {
    try {
      await signOut();
    } catch {
      /* ignore */
    }
    window.location.reload();
  };

  const handleUpgrade = async () => {
    setCheckoutLoading(true);
    try {
      await startCheckout({ interval: 'MONTHLY', addonQuantity: 0 });
    } catch {
      show("Couldn't start checkout. Please try again.", 'error');
      setCheckoutLoading(false);
    }
  };

  return (
    <>
      <StyledSnackbar {...snackbarProps} />
      <Dialog
        open={gated}
        fullWidth
        maxWidth="md"
        disableEscapeKeyDown
        disableEnforceFocus
        onClose={(_, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        }}
        slotProps={{
          backdrop: { className: 'billing-onboarding-backdrop' },
        }}
        PaperProps={{
          className: 'billing-onboarding-panel',
          sx: { bgcolor: 'background.paper', color: 'text.primary' },
        }}
      >
        <div className="billing-onboarding-scroll">
          <div className="billing-onboarding-content">
            <div className="billing-onboarding-brand">
              <ThemeLogo className="billing-onboarding-logo" />
            </div>

            {settling ? (
              <div className="billing-gate-settling">
                <CircularProgress />
                <h2 className="billing-gate-title">Finalizing your upgrade…</h2>
                <p className="billing-gate-copy">
                  Payment received — activating your Standard plan. This takes a few seconds.
                </p>
              </div>
            ) : (
            <>
            <div className="billing-gate-header">
              <h2 className="billing-gate-title">
                {isInvalid ? 'Your API key stopped working' : 'Connect your AI provider to get started'}
              </h2>
              {isInvalid ? (
                <p className="billing-gate-copy">{INVALID_BODY}</p>
              ) : (
                <div className="billing-gate-copy">
                  {PENDING_INTRO.map((paragraph) => (
                    <p key={paragraph}>{paragraph}</p>
                  ))}
                </div>
              )}
            </div>

            <NodeSection title="Provider API key">
              <OnboardingProviderKeyForm
                onValidated={handleValidated}
                validating={validating}
                setValidating={setValidating}
              />
            </NodeSection>

            <NodeSection title="No key? Upgrade to Standard">
              <div className="billing-gate-standard">
                <p className="billing-plan-description">
                  {PLAN_CATALOG.STANDARD.description}
                </p>
                <ul className="billing-plan-features">
                  {STANDARD_HIGHLIGHTS.map((feature) => (
                    <li key={feature} className="billing-plan-feature">
                      <span className="billing-plan-feature-mark" aria-hidden>
                        <CheckIcon fontSize="inherit" />
                      </span>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <div className="billing-gate-actions">
                  <Button
                    variant="contained"
                    fullWidth
                    className="billing-free-upgrade-primary"
                    disabled={checkoutLoading}
                    onClick={handleUpgrade}
                  >
                    {checkoutLoading ? 'Starting checkout…' : 'Upgrade to Standard'}
                  </Button>
                  <Button
                    variant="text"
                    fullWidth
                    component="a"
                    href="mailto:sales@workflow86.com"
                  >
                    Contact sales for Enterprise
                  </Button>
                </div>
              </div>
            </NodeSection>
            </>
            )}

            <button type="button" className="billing-onboarding-logout" onClick={handleLogout}>
              Log out
            </button>
          </div>
        </div>
      </Dialog>
    </>
  );
}
